import { create } from 'zustand'

export type CursorShape = 'arrow' | 'hand' | 'ibeam' | 'wait' | 'crosshair' | 'resize'

export interface MouseEvent {
    t: number      // timestamp (ms since recording start)
    x: number      // x coordinate
    y: number      // y coordinate
    type: 'move' | 'click' | 'dwell' | 'shape'
    shape?: CursorShape  // only present on type:'shape' events
}

export type RecordingStatus = 'idle' | 'recording' | 'paused' | 'stopped'

interface RecordingState {
    // State
    status: RecordingStatus
    duration: number           // in seconds
    blob: Blob | null
    cameraBlob: Blob | null    // Added camera recording
    mouseData: MouseEvent[]
    originalFrameRate: number
    screenWidth: number        // Width of the recorded screen area
    screenHeight: number       // Height of the recorded screen area
    isExtension: boolean      // Whether this was recorded via the extension
    isDesktop: boolean        // Whether this was recorded via the Electron desktop app
    sourceTitle: string | null // Tab/window title captured at recording start

    // Actions
    setStatus: (status: RecordingStatus) => void
    setDuration: (duration: number) => void
    incrementDuration: () => void
    setBlob: (blob: Blob) => void
    setCameraBlob: (blob: Blob) => void // Added action
    addMouseEvent: (event: MouseEvent) => void
    setMouseData: (data: MouseEvent[]) => void
    setOriginalFrameRate: (rate: number) => void
    setScreenDimensions: (w: number, h: number) => void
    setExtension: (isExtension: boolean) => void
    setDesktop: (isDesktop: boolean) => void
    setSourceTitle: (title: string | null) => void
    reset: () => void
}

const initialState = {
    status: 'idle' as RecordingStatus,
    duration: 0,
    blob: null,
    cameraBlob: null,
    mouseData: [],
    originalFrameRate: 30,
    screenWidth: 1920,
    screenHeight: 1080,
    isExtension: false,
    isDesktop: false,
    sourceTitle: null,
}

export const useRecordingStore = create<RecordingState>((set) => ({
    ...initialState,

    setStatus: (status) => set({ status }),

    setDuration: (duration) => set({ duration }),

    incrementDuration: () => set((state) => ({ duration: state.duration + 1 })),

    setBlob: (blob) => set({ blob }),

    setCameraBlob: (cameraBlob) => set({ cameraBlob }),

    addMouseEvent: (event) => set((state) => ({
        mouseData: [...state.mouseData, event]
    })),

    setMouseData: (mouseData) => set({ mouseData }),
    setOriginalFrameRate: (originalFrameRate) => set({ originalFrameRate }),
    setScreenDimensions: (screenWidth, screenHeight) => set({ screenWidth, screenHeight }),
    setExtension: (isExtension) => set({ isExtension }),
    setDesktop: (isDesktop) => set({ isDesktop }),
    setSourceTitle: (sourceTitle) => set({ sourceTitle }),
    reset: () => set(initialState),
}))
